import groupBy from 'lodash/groupBy'
import head from 'lodash/head'
import map from 'lodash/map'
import omit from 'lodash/omit'
import reduce from 'lodash/reduce'
import size from 'lodash/size'
import snakeCase from 'lodash/snakeCase'
import split from 'lodash/split'
import uniq from 'lodash/uniq'
import values from 'lodash/values'
import getConfig from 'next/config'

import { Options, ProductSorting, Result } from '@Globals/Algolia/Types'
import { Group } from '@Globals/SplitTests/types'

// the main index uses the sorting_v1 group a ranking, so only b/c/d need replicas
const replicaSuffix = (sortingGroup: Group, sorting: ProductSorting) => {
  switch (sorting) {
    case ProductSorting.Default:
      switch (sortingGroup) {
        case 'b':
        case 'c':
        case 'd':
          return `_${sortingGroup}`
        default:
          return ''
      }
    default:
      return `_${sorting}`
  }
}

const generateIndex = ({ entity, locale, sorting, sortingGroup }: Options) => {
  const { publicRuntimeConfig } = getConfig()

  // Looks have a single newest-first index with no sort replicas.
  if (entity === 'looks') {
    return `looks_${snakeCase(locale)}${publicRuntimeConfig.algoliaIndexSuffix}`
  }

  return `products_${snakeCase(locale)}${publicRuntimeConfig.algoliaIndexSuffix}${replicaSuffix(sortingGroup, sorting)}`
}

const generateParams = (
  {
    distinct,
    facetFilters,
    facets,
    filters,
    grouped,
    hitsPerPage,
    numericFilters,
    optionalFilters,
    page,
    pagination,
    query,
    sorting,
  }: Options,
  singleFacet?: string,
  isSSR = false,
) => {
  const searchParams = new URLSearchParams()
  if (!isSSR && !pagination) {
    searchParams.set('attributesToRetrieve', JSON.stringify(['objectID']))
  } else {
    // groupProducts is a heavy attribute the index deliberately does not
    // exclude — retrieval is owned here. Only the grouped experience needs it.
    searchParams.set('attributesToRetrieve', JSON.stringify(grouped ? ['*'] : ['*', '-groupProducts']))
  }

  if (distinct) {
    // one hit per colour group; relies on attributeForDistinct: groupID in
    // the index settings. Applied to the facet-count queries too, so counts
    // match the collapsed result set.
    searchParams.set('distinct', 'true')
  }

  if (singleFacet) {
    searchParams.set('facets', JSON.stringify([singleFacet]))
  } else if (facets.length > 0) {
    searchParams.set('facets', JSON.stringify(facets))
  }
  if (facetFilters?.length > 0) {
    searchParams.set('facetFilters', JSON.stringify(
      values(
        omit(
          groupBy(
            facetFilters,
            item => head(split(item, ':')),
          ),
          singleFacet || '',
        ),
      ),
    ))
  }

  if (filters) {
    searchParams.set('filters', filters)
  }

  if (size(numericFilters) !== 0) {
    searchParams.set('numericFilters', JSON.stringify(
      reduce(numericFilters, (memo, { max, min }, key) => {
        if (min === max) {
          return [...memo, `${key} = ${min}`]
        }

        return [...memo, `${key} >= ${min}`, `${key} <= ${max}`]
      }, []),
    ))
  }

  if (optionalFilters?.length > 0 && sorting === ProductSorting.Default) {
    searchParams.set('optionalFilters', JSON.stringify(optionalFilters))
  }

  searchParams.set('hitsPerPage', singleFacet ? '0' : (hitsPerPage || '1000'))

  if (pagination && page && page >= 1) {
    searchParams.set('page', (page - 1).toString())
  }

  if (query) {
    searchParams.set('query', query)
    searchParams.set('optionalWords', query)
  }

  return searchParams.toString()
}

export const generateURL = () => {
  const { publicRuntimeConfig } = getConfig()
  const url = new URL(`https://${publicRuntimeConfig.algoliaAppID.toLowerCase()}-dsn.algolia.net/1/indexes/*/queries`)
  url.searchParams.set('x-algolia-application-id', publicRuntimeConfig.algoliaAppID)
  url.searchParams.set('x-algolia-api-key', publicRuntimeConfig.algoliaAPIKey)
  return url.toString()
}

export const generateRequests = (options: Options, isSSR?: boolean) => {
  const indexName = generateIndex(options)

  return [
    {
      indexName,
      params: generateParams(options, undefined, isSSR),
    },
    ...map(
      uniq(map(options.facetFilters, item => head(split(item, ':')))),
      singleFacet => ({
        indexName,
        params: generateParams(options, singleFacet),
      }),
    ),
  ]
}

export const parseResults = (results: any, isSSR: boolean | undefined, options: Options): Result => {
  if (results && results.length > 0) {
    return {
      facets: reduce(
        results,
        (memo, item) => ({
          ...memo,
          ...item.facets || {},
        }),
        {},
      ),
      facetsStats: isSSR ? results[0]?.facets_stats || {} : {},
      hits: (isSSR || options.pagination) ? results[0]?.hits || [] : [],
      objectIDs: map(results[0]?.hits, 'objectID'),
      page: results[0]?.page || 0,
      pages: results[0]?.nbPages || 0,
      totalHits: results[0]?.nbHits || 0,
    }
  }

  return {
    facets: {},
    facetsStats: {},
    hits: [],
    objectIDs: [],
    page: 0,
    pages: 0,
    totalHits: 0,
  }
}

export const algoliaFetcher = async (options: Options, isSSR?: boolean): Promise<Result> => {
  const url = generateURL()
  const fetchedData = await fetch(url, {
    method: 'POST',
    headers: new Headers({
      'Content-Type': 'application/json; charset=UTF-8',
    }),
    body: JSON.stringify({
      requests: generateRequests(options, isSSR),
    }),
  })

  if (fetchedData.status !== 200) {
    throw new Error('Failed to fetch from Algolia')
  }

  try {
    const data = await fetchedData.json()
    return parseResults(data?.results, isSSR, options)
  } catch (e) {
    throw new Error('Something went wrong trying to get data from Algolia')
  }
}
